import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Array<{ text: string; sender: 'user' | 'agent'; timestamp: Date }>>([
    {
      text: 'Hi! 👋 How can I help you today?',
      sender: 'agent',
      timestamp: new Date()
    }
  ]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Simulate agent availability
  const isAgentOnline = true;

  // Auto-open after delay (first visit only)
  useEffect(() => {
    const hasSeenChat = sessionStorage.getItem('hasSeenLiveChat');
    if (!hasSeenChat) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem('hasSeenLiveChat', 'true');
      }, 10000); // Open after 10 seconds
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSendMessage = () => {
    if (!message.trim()) return;

    const newMessage = {
      text: message,
      sender: 'user' as const,
      timestamp: new Date()
    };

    setMessages([...messages, newMessage]);
    setMessage('');

    // Simulate agent response
    setTimeout(() => {
      const agentResponse = {
        text: getAutomatedResponse(message),
        sender: 'agent' as const,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1000);
  };

  const getAutomatedResponse = (userMessage: string): string => {
    const msg = userMessage.toLowerCase();
    
    if (msg.includes('price') || msg.includes('cost') || msg.includes('how much')) {
      return 'Our therapy sessions start from $25. You can also save up to 15% by prepaying into your wallet. Would you like to learn more about pricing?';
    }
    if (msg.includes('book') || msg.includes('appointment') || msg.includes('session')) {
      return 'Great! You can book a session in just 2 steps. Would you like me to guide you through the process?';
    }
    if (msg.includes('therapist') || msg.includes('find')) {
      return 'We have 5,000+ certified therapists in 150+ countries. You can search by specialty, language, or location. Would you like help finding the right therapist?';
    }
    if (msg.includes('online') || msg.includes('video')) {
      return 'Yes! Most of our therapists offer secure online video sessions. You can also filter for in-person sessions if you prefer.';
    }
    
    return 'Thanks for your message! A support agent will respond shortly. In the meantime, check out our FAQ or browse our therapist directory.';
  };

  const quickActions = [
    'How do I book a session?',
    'What are your prices?',
    'Find a therapist'
  ];

  return (
    <>
      {/* Chat Button */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="fixed bottom-6 right-6 z-50"
      >
        {!isOpen && (
          <Button
            size="lg"
            className="rounded-full h-16 w-16 shadow-2xl relative group"
            onClick={() => setIsOpen(true)}
          >
            <MessageCircle className="h-6 w-6" />
            {unreadCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-6 w-6 rounded-full p-0 flex items-center justify-center">
                {unreadCount}
              </Badge>
            )}
            <span className="absolute -top-10 right-0 bg-foreground text-background px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
              Need help? Chat with us!
            </span>
            {isAgentOnline && (
              <span className="absolute bottom-0 right-0 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500 border-2 border-background"></span>
              </span>
            )}
          </Button>
        )}
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 'auto' : '500px'
            }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)]"
          >
            <Card className="shadow-2xl border-2 flex flex-col h-full">
              {/* Header */}
              <CardHeader className="pb-3 border-b bg-gradient-to-r from-primary/5 to-accent/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <MessageCircle className="h-5 w-5 text-primary" />
                      </div>
                      {isAgentOnline && (
                        <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-background rounded-full"></span>
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-base">Support Chat</CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {isAgentOnline ? 'We\'re online' : 'We\'ll respond soon'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setIsMinimized(!isMinimized)}
                    >
                      <Minimize2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setIsOpen(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              {!isMinimized && (
                <>
                  {/* Messages */}
                  <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.sender === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm">{msg.text}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* Quick Actions */}
                    {messages.length === 1 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground text-center">Quick questions:</p>
                        {quickActions.map((action, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            className="w-full text-xs h-auto py-2"
                            onClick={() => {
                              setMessage(action);
                              setTimeout(() => handleSendMessage(), 100);
                            }}
                          >
                            {action}
                          </Button>
                        ))}
                      </div>
                    )}
                  </CardContent>

                  {/* Input */}
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Type your message..."
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        onClick={handleSendMessage}
                        disabled={!message.trim()}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Powered by Global Therapy Hub Support
                    </p>
                  </div>
                </>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
